import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteRow, insertRow, listRows, updateRow } from "@/lib/adminApi";
import {
  COUPON_STATUS,
  CUSTOMER_TYPES,
  DISCOUNT_TYPE,
  commerceMeta,
  commerceOptions,
  type Coupon,
} from "@/lib/adminCommerce";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

const EMPTY_COUPON = {
  code: "",
  discount_type: "percent",
  discount_value: 10,
  min_booking: 0,
  max_discount: 500,
  valid_from: "",
  valid_to: "",
  usage_limit: 100,
  farm: "All farms",
  experience: "All experiences",
  customer_type: "All",
  status: "active",
};

function discountLabel(row: Coupon): string {
  return row.discount_type === "percent" ? `${row.discount_value}%` : `₹${row.discount_value}`;
}

export default function AdminCoupons() {
  const [rows, setRows] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_COUPON });
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Coupon>("coupons", "code"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load coupons.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<Coupon>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("coupons", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const createCoupon = async () => {
    if (!draft.code.trim()) {
      setError("A coupon code is required.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<Coupon>("coupons", {
        ...draft,
        code: draft.code.trim().toUpperCase(),
        used_count: 0,
        valid_from: draft.valid_from || null,
        valid_to: draft.valid_to || null,
      });
      setRows((list) => [created, ...list]);
      setCreateOpen(false);
      setDraft({ ...EMPTY_COUPON });
      notify(`Coupon ${created.code} created.`);
    } catch {
      setError("We couldn't create that coupon. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number, code: string) => {
    try {
      await deleteRow("coupons", id);
      setRows((list) => list.filter((row) => row.id !== id));
      setSelectedId(null);
      notify(`Coupon ${code} deleted.`);
    } catch {
      setError("We couldn't delete that coupon. Please try again.");
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.code, row.farm || "", row.experience || "", row.customer_type || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, statusFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;
  const totalRedemptions = rows.reduce((sum, row) => sum + Number(row.used_count || 0), 0);

  const columns: Column<Coupon>[] = [
    {
      key: "code",
      header: "Coupon",
      primary: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-accent-100 text-accent-900 shrink-0 flex items-center justify-center">
            <i className="ri-coupon-3-line"></i>
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.code}</p>
            <p className="text-xs text-foreground-500 mt-0.5 capitalize">
              {commerceMeta(DISCOUNT_TYPE, row.discount_type).label}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "value",
      header: "Discount",
      render: (row) => (
        <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{discountLabel(row)}</span>
      ),
    },
    {
      key: "min",
      header: "Min booking",
      render: (row) => (
        <span className="text-sm text-foreground-700 whitespace-nowrap">₹{row.min_booking}</span>
      ),
    },
    {
      key: "max",
      header: "Max discount",
      render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">₹{row.max_discount}</span>,
    },
    {
      key: "valid",
      header: "Valid",
      render: (row) => (
        <span className="text-xs text-foreground-500 whitespace-nowrap">
          {formatDate(row.valid_from)} – {formatDate(row.valid_to)}
        </span>
      ),
    },
    {
      key: "usage",
      header: "Usage",
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 rounded-full bg-background-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary-500"
              style={{ width: `${row.usage_limit ? Math.min(100, (row.used_count / row.usage_limit) * 100) : 0}%` }}
            ></div>
          </div>
          <span className="text-xs text-foreground-600 whitespace-nowrap">
            {row.used_count}/{row.usage_limit}
          </span>
        </div>
      ),
    },
    { key: "status", header: "Status", render: (row) => <StatusPill {...commerceMeta(COUPON_STATUS, row.status)} /> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Commerce"
        title="Coupons"
        subtitle="Discount codes for campaigns, families, schools and festivals — scoped by farm, experience and customer type."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> Create Coupon
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total coupons", value: rows.length, icon: "ri-coupon-3-line", tone: "primary" },
          {
            label: "Active",
            value: rows.filter((row) => row.status === "active").length,
            icon: "ri-checkbox-circle-line",
            tone: "primary",
          },
          { label: "Total redemptions", value: totalRedemptions, icon: "ri-hand-coin-line", tone: "secondary" },
          {
            label: "Paused / expired",
            value: rows.filter((row) => row.status !== "active").length,
            icon: "ri-pause-circle-line",
            tone: "accent",
          },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by code, farm, experience…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...commerceOptions(COUPON_STATUS)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} coupons</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading coupons…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-coupon-3-line"
            title={rows.length === 0 ? "No coupons yet" : "No coupons match"}
            message="Create a coupon to run a discount campaign."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.code || "Coupon"}
        subtitle="Coupon detail"
        footer={
          selected ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  update(
                    selected.id,
                    { status: selected.status === "active" ? "paused" : "active" },
                    selected.status === "active" ? "Coupon paused." : "Coupon reactivated.",
                  )
                }
                className={btnPrimary}
              >
                <i className={selected.status === "active" ? "ri-pause-line" : "ri-play-line"}></i>
                {selected.status === "active" ? "Pause" : "Activate"}
              </button>
              <button type="button" onClick={() => remove(selected.id, selected.code)} className={btnGhost}>
                <i className="ri-delete-bin-line"></i> Delete
              </button>
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...commerceMeta(COUPON_STATUS, selected.status)} />
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground-800 bg-background-100 border border-background-300 px-2.5 py-1 rounded-full">
                {discountLabel(selected)} off
              </span>
            </div>

            <DetailList
              items={[
                { icon: "ri-focus-3-line", label: "Farm", value: selected.farm || "All farms" },
                { icon: "ri-compass-3-line", label: "Experience", value: selected.experience || "All experiences" },
                { icon: "ri-user-3-line", label: "Customer type", value: selected.customer_type || "All" },
                { icon: "ri-money-rupee-circle-line", label: "Minimum booking", value: `₹${selected.min_booking}` },
                { icon: "ri-price-tag-3-line", label: "Maximum discount", value: `₹${selected.max_discount}` },
                { icon: "ri-calendar-line", label: "Valid from", value: formatDate(selected.valid_from) },
                { icon: "ri-calendar-check-line", label: "Valid to", value: formatDate(selected.valid_to) },
              ]}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Discount type">
                <select
                  value={selected.discount_type}
                  onChange={(event) => update(selected.id, { discount_type: event.target.value })}
                  className={fieldInput}
                >
                  {commerceOptions(DISCOUNT_TYPE).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select
                  value={selected.status}
                  onChange={(event) => update(selected.id, { status: event.target.value }, "Coupon status updated.")}
                  className={fieldInput}
                >
                  {commerceOptions(COUPON_STATUS).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-background-100 border border-background-200 rounded-md p-3">
                <p className="font-heading text-xl font-semibold text-foreground-950">{selected.used_count}</p>
                <p className="text-[11px] text-foreground-500 mt-0.5">Times used</p>
              </div>
              <div className="bg-background-100 border border-background-200 rounded-md p-3">
                <p className="font-heading text-xl font-semibold text-foreground-950">{selected.usage_limit}</p>
                <p className="text-[11px] text-foreground-500 mt-0.5">Usage limit</p>
              </div>
            </div>
          </>
        ) : null}
      </Drawer>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create a coupon"
        subtitle="New coupon"
        size="lg"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>
              Cancel
            </button>
            <button type="button" onClick={createCoupon} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Create coupon
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Coupon code">
            <input
              value={draft.code}
              onChange={(event) => setDraft({ ...draft, code: event.target.value.toUpperCase() })}
              placeholder="e.g. HARVEST10"
              className={textInput}
            />
          </TextField>
          <TextField label="Discount type">
            <select
              value={draft.discount_type}
              onChange={(event) => setDraft({ ...draft, discount_type: event.target.value })}
              className={fieldInput}
            >
              {commerceOptions(DISCOUNT_TYPE).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </TextField>
          <TextField label="Discount value">
            <input
              type="number"
              value={draft.discount_value}
              onChange={(event) => setDraft({ ...draft, discount_value: Number(event.target.value) })}
              className={textInput}
            />
          </TextField>
          <TextField label="Maximum discount (₹)">
            <input
              type="number"
              value={draft.max_discount}
              onChange={(event) => setDraft({ ...draft, max_discount: Number(event.target.value) })}
              className={textInput}
            />
          </TextField>
          <TextField label="Minimum booking (₹)">
            <input
              type="number"
              value={draft.min_booking}
              onChange={(event) => setDraft({ ...draft, min_booking: Number(event.target.value) })}
              className={textInput}
            />
          </TextField>
          <TextField label="Usage limit">
            <input
              type="number"
              value={draft.usage_limit}
              onChange={(event) => setDraft({ ...draft, usage_limit: Number(event.target.value) })}
              className={textInput}
            />
          </TextField>
          <TextField label="Valid from">
            <input
              type="date"
              value={draft.valid_from}
              onChange={(event) => setDraft({ ...draft, valid_from: event.target.value })}
              className={textInput}
            />
          </TextField>
          <TextField label="Valid to">
            <input
              type="date"
              value={draft.valid_to}
              onChange={(event) => setDraft({ ...draft, valid_to: event.target.value })}
              className={textInput}
            />
          </TextField>
          <TextField label="Farm">
            <input
              value={draft.farm}
              onChange={(event) => setDraft({ ...draft, farm: event.target.value })}
              placeholder="All farms"
              className={textInput}
            />
          </TextField>
          <TextField label="Experience">
            <input
              value={draft.experience}
              onChange={(event) => setDraft({ ...draft, experience: event.target.value })}
              placeholder="All experiences"
              className={textInput}
            />
          </TextField>
          <TextField label="Customer type">
            <select
              value={draft.customer_type}
              onChange={(event) => setDraft({ ...draft, customer_type: event.target.value })}
              className={fieldInput}
            >
              {CUSTOMER_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </TextField>
          <TextField label="Status">
            <select
              value={draft.status}
              onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              className={fieldInput}
            >
              {commerceOptions(COUPON_STATUS).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </TextField>
        </div>
      </Modal>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>
      {node}
    </div>
  );
}